/**
 * Registers the service worker and offers a reload when a newer build lands.
 *
 * The app is offline-first: the service worker precaches every page, script
 * and stylesheet, so a tab left open on a phone will keep serving the build it
 * first met and never go back to the network to find out otherwise. That is
 * the correct behaviour for a workout app in a basement gym, and it is also
 * why a shipped fix can appear not to have shipped.
 *
 * The usual "update ready" pattern watches `registration.waiting`, but this
 * project builds its worker with `skipWaiting: true` (see build-sw.cjs), so a
 * new worker never waits -- it installs and activates straight through, and
 * `waiting` is null every time you look at it. What is observable is the
 * moment the new worker reaches `installed` while an older one is still
 * controlling the page, so that is what this listens for, with
 * `controllerchange` as a second chance in case the state change is missed.
 *
 * Nothing reloads on its own. The new assets are already on disk, but swapping
 * them under someone mid-set would lose their place in the round.
 */

/** How often an open tab asks whether a newer worker has been published. @type {number} */
const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000;

/**
 * How long a press waits for the new worker to take over before reloading
 * anyway. A tap that appears to do nothing is worse than a reload that lands
 * on the old build, which the next press will fix.
 * @type {number}
 */
const ACTIVATION_TIMEOUT = 3000;

/**
 * Wires the update prompt to the service worker lifecycle.
 *
 * @param {object} [options]
 * @param {Document} [options.root] Document holding the prompt shell.
 * @param {ServiceWorkerContainer|null} [options.container] Injected for tests.
 * @param {() => void} [options.reload] Injected for tests.
 * @param {string} [options.scriptUrl] Worker URL.
 * @param {number} [options.checkInterval] Milliseconds between update checks.
 * @returns {{show: () => void, check: () => void}|null} Null when unsupported.
 */
export function wireUpdatePrompt({
  root = document,
  container = typeof navigator !== 'undefined' ? navigator.serviceWorker : null,
  reload = () => window.location.reload(),
  scriptUrl = '/sw.js',
  checkInterval = UPDATE_CHECK_INTERVAL,
} = {}) {
  const prompt = root.getElementById('update-prompt');
  if (!container || !prompt) return null;

  const button = prompt.querySelector('[data-update-reload]');
  const hint = prompt.querySelector('[data-update-hint]');
  let shown = false;
  let busy = false;

  /* The worker that will replace the current one, from `updatefound` or from
     whatever was already part-way through when this ran. Held because the
     press has to wait for it. */
  let pending = null;
  let controllerChanged = false;

  /* A first-ever visit installs a worker too, and that is not an update -- it
     is the app arriving. `controller` is null until something is in charge, so
     its presence is what separates "replaced" from "installed for the first
     time". Captured now because `controllerchange` is precisely the event that
     invalidates it. */
  const hadController = !!container.controller;

  const show = () => {
    if (shown) return;
    shown = true;
    prompt.hidden = false;
  };

  /**
   * Reloads once the new worker is the one that will answer the navigation.
   *
   * The prompt appears when the new worker reaches `installed`, which is one
   * state short of being in charge. Reloading there sends the navigation to
   * the worker that is still active -- the old one -- which answers it out of
   * the old precache, so the page comes back looking exactly as it did. That
   * is the bug this exists to prevent: it looked like nothing had happened,
   * and only a force-reload (which bypasses the worker entirely) showed the
   * new build.
   *
   * `activated` is the gate rather than `controllerchange`, because an
   * activated worker answers fresh navigations for its scope whether or not it
   * has claimed this particular page yet. `controllerchange` is still honoured
   * as the earlier of the two signals.
   */
  const applyUpdate = () => {
    if (controllerChanged || !pending) {
      // Already in charge -- there is nothing left to wait for.
      reload();
      return;
    }

    let reloaded = false;
    const go = () => {
      if (reloaded) return;
      reloaded = true;
      reload();
    };

    container.addEventListener('controllerchange', go);
    pending.addEventListener('statechange', () => {
      if (pending.state === 'activated') go();
    });
    if (pending.state === 'activated') go();
    setTimeout(go, ACTIVATION_TIMEOUT);
  };

  if (button) {
    button.addEventListener('click', () => {
      if (busy) return;
      busy = true;
      /* The wait is usually imperceptible, but it is not guaranteed to be, and
         a press with no visible answer invites a second press. The prompt is a
         live region, so this is announced as well as seen. */
      if (hint) hint.textContent = 'Updating…';
      applyUpdate();
    });
  }

  let registration = null;
  const check = () => {
    // An update check is a network request. Offline it rejects, which is the
    // normal state for this app rather than an error worth surfacing.
    registration?.update().catch(() => {});
  };

  container.addEventListener('controllerchange', () => {
    controllerChanged = true;
    if (hadController) show();
  });

  container
    .register(scriptUrl)
    .then((reg) => {
      registration = reg;
      // A worker may already have been part-way through when this ran.
      pending = reg.installing ?? reg.waiting ?? null;

      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        pending = installing;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && container.controller) show();
        });
      });

      /* A phone that never closes the tab would otherwise only check on the
         next cold start. Both triggers are cheap: an update check is one
         conditional request for the worker script. */
      if (checkInterval > 0) setInterval(check, checkInterval);
      root.addEventListener('visibilitychange', () => {
        if (!root.hidden) check();
      });
    })
    .catch((err) => console.warn('[sw] registration failed', err));

  return { show, check };
}

export default wireUpdatePrompt;
